import { Emitter } from "@core/events/emitter";

/**
 * Frame — global per-frame tick source.
 *
 * Replaces Unity's `Update()` / `LateUpdate()` callbacks used across the shared
 * runtime (see `shared-application-code/Shared/Assets/Campfire/Runtime/Infrastructure/Frame.cs`
 * for the Unity equivalent). Anything that needs a per-frame pulse (tools,
 * modifiers, interpolators, voice visualizer) subscribes here instead of
 * subclassing a MonoBehaviour.
 *
 * Driven by the Three.js render loop in `runtime/engine/engineService.ts`.
 */

export interface FrameTick {
  /** Seconds elapsed since previous tick. */
  readonly deltaSeconds: number;
  /** Seconds elapsed since engine start. */
  readonly elapsedSeconds: number;
}

export class FrameModel {
  readonly onTick = new Emitter<FrameTick>();
  readonly onLateTick = new Emitter<FrameTick>();

  private m_elapsed = 0;

  tick(deltaSeconds: number): void {
    this.m_elapsed += deltaSeconds;
    const payload: FrameTick = { deltaSeconds, elapsedSeconds: this.m_elapsed };
    this.onTick.emit(payload);
  }

  lateTick(): void {
    const payload: FrameTick = { deltaSeconds: 0, elapsedSeconds: this.m_elapsed };
    this.onLateTick.emit(payload);
  }

  get elapsedSeconds(): number {
    return this.m_elapsed;
  }
}

export const FRAME_MODEL = Symbol.for("FrameModel");
